/**
 * i18n Integration and Advanced Features
 *
 * Handles i18n library integration, translation observers, and browser translation
 */

import i18n from 'i18next';
import { systemLogger } from '../logging';
import { type SupportedLanguage } from './constants';
import { syncLanguageToStorage } from './storage';
import { saveUserLanguageToDatabase } from './database';

/**
 * Change language in application
 */
export async function changeLanguage(
  language: SupportedLanguage,
  saveToDatabase: boolean = true
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    // Change i18n language
    await i18n.changeLanguage(language);

    // Sync to browser storage
    syncLanguageToStorage(language);

    // Save to database if requested
    if (saveToDatabase) {
      const result = await saveUserLanguageToDatabase(language);
      if (!result.success) {
        systemLogger.warn('Failed to save language to database', result.error);
        // UI updated successfully (i18n and storage), but database save failed
        return {
          success: false,
          error: result.error || 'Failed to save language preference to database'
        };
      }
    }

    return { success: true };
  } catch (error) {
    systemLogger.error('Language service operation failed', error);
    return { success: false, error: 'Failed to change language' };
  }
}

/**
 * Initialize language system
 */
export function initializeLanguage(
  initialLanguage: SupportedLanguage = 'en'
): void {
  try {
    syncLanguageToStorage(initialLanguage);
  } catch (error) {
    systemLogger.error('Language service operation failed', error);
  }
}

/**
 * Set translate attribute on document to control browser translation
 */
export function setTranslateAttribute(allowed: boolean): void {
  try {
    const htmlElement = document.documentElement;
    if (allowed) {
      htmlElement.removeAttribute('translate');
    } else {
      htmlElement.setAttribute('translate', 'no');
    }
  } catch (error) {
    systemLogger.warn('Translation attribute update failed', error);
  }
}

/**
 * Detect if browser translation is active
 */
export function isBrowserTranslationActive(): boolean {
  try {
    const htmlElement = document.documentElement;

    // Check for Google Translate indicators
    const hasGoogleTranslateClass = htmlElement.classList.contains('translated-ltr') ||
                                   htmlElement.classList.contains('translated-rtl');

    // Check for translate attribute
    const translateAttribute = htmlElement.getAttribute('translate');

    // Check for other translation service indicators
    const hasTranslationScript = document.querySelector('script[src*="translate.google"]') !== null;

    return hasGoogleTranslateClass || translateAttribute === 'yes' || hasTranslationScript;
  } catch (error) {
    systemLogger.warn('Translation detection failed', error);
    return false;
  }
}

/**
 * Store original language before browser translation
 */
export function storeOriginalLanguage(): void {
  try {
    const currentLang = i18n.language;
    sessionStorage.setItem('originalLanguage', currentLang);
  } catch (error) {
    systemLogger.warn('Original language storage failed', error);
  }
}

/**
 * Create mutation observer for translation detection
 */
export function createTranslationObserver(
  callback: (_isTranslated: boolean) => void
): MutationObserver | null {
  try {
    const observer = new MutationObserver(() => {
      const isTranslated = isBrowserTranslationActive();
      callback(isTranslated);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'translate'],
      childList: true,
      subtree: false
    });

    return observer;
  } catch (error) {
    systemLogger.warn('Translation observer creation failed', error);
    return null;
  }
}